import React, { createContext, useContext } from 'react';
import {
  Accordion as ChakraAccordion,
  AccordionProps as ChakraAccordionProps,
  AccordionItem as ChakraAccordionItem,
  AccordionItemProps as ChakraAccordionItemProps,
  AccordionButton as ChakraAccordionButton,
  AccordionButtonProps as ChakraButtonProps,
  AccordionPanel as ChakraAccordionPanel,
  AccordionPanelProps as ChakraAccordionPanelProps,
  AccordionIcon,
  Box,
  Text,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import {
  getAccordionTransparentBorderColor,
  getAccordionIconMargins,
  getContentTitleStyles,
  getAccordionPanelStyles,
} from './accordion.theme';

export interface AccordionProps extends ChakraAccordionProps {
  children?: React.ReactNode;
  /**
   * If `true`, each accordion item will display a colored bottom border.
   * @default false
   */
  showUnderline?: boolean;
  /**
   * If `true`, any expanded accordion item can be collapsed again.
   * @default true
   */
  allowToggle?: boolean;
  /** The color mode that the button will display.
   * @default light
   */
  colorMode?: ColorMode;
}

export interface AccordionButtonProps extends ChakraButtonProps {
  children?: React.ReactNode;
  icon?: React.ReactElement;
}

export interface AccordionPanelProps extends ChakraAccordionPanelProps {
  children?: React.ReactNode;
  /**
   * Optional title for the content body.
   */
  contentTitle?: string;
}

const AccordionContext = createContext({
  showUnderline: false,
});

/**
 * The `Accordion` will display a list of high-level options that can expand/collapse to reveal more information.
 */
export const Accordion = forwardRef<AccordionProps, 'div'>(
  (
    {
      children,
      showUnderline = false,
      allowToggle = true,
      colorMode: propColorMode,
      ...rest
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    return (
      <AccordionContext.Provider value={{ showUnderline }}>
        <ChakraAccordion
          ref={ref}
          colorScheme={colorMode}
          allowToggle={allowToggle}
          {...rest}
        >
          {children}
        </ChakraAccordion>
      </AccordionContext.Provider>
    );
  }
);

export const AccordionButton = forwardRef<AccordionButtonProps, 'button'>(
  ({ children, icon, ...rest }, ref) => {
    return (
      <ChakraAccordionButton ref={ref} {...rest}>
        {icon && <Box sx={getAccordionIconMargins()}>{icon}</Box>}
        {children}
        <AccordionIcon />
      </ChakraAccordionButton>
    );
  }
);

export const AccordionItem = forwardRef<ChakraAccordionItemProps, 'div'>(
  ({ children, ...rest }, ref) => {
    const { showUnderline } = useContext(AccordionContext);

    const borderColor = showUnderline
      ? undefined
      : getAccordionTransparentBorderColor();

    return (
      <ChakraAccordionItem
        ref={ref}
        data-testid="accordion-item"
        borderColor={borderColor}
        {...rest}
      >
        {children}
      </ChakraAccordionItem>
    );
  }
);

export const AccordionPanel = forwardRef<AccordionPanelProps, 'div'>(
  ({ children, contentTitle, ...rest }, ref) => {
    return (
      <ChakraAccordionPanel ref={ref} sx={getAccordionPanelStyles()} {...rest}>
        {contentTitle && (
          <Text sx={getContentTitleStyles()}>
            {contentTitle}
            <br />
          </Text>
        )}

        {children}
      </ChakraAccordionPanel>
    );
  }
);
